import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { DashboardWidgetStatus } from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";

export type DashboardWidgetIssueSeverity = "error" | "warning" | "info";

export interface DashboardWidgetIssue {
  severity: DashboardWidgetIssueSeverity;
  title: string;
  description: string;
}

export interface DashboardWidgetIssueDetails {
  unavailableAreaNames?: readonly string[];
  notConfiguredAreaNames?: readonly string[];
}

function formatAreaNames(areaNames: readonly string[]): string {
  return areaNames.length === 1
    ? areaNames[0]
    : `${areaNames.slice(0, -1).join(", ")} and ${areaNames[areaNames.length - 1]}`;
}

/** Returns issues that need attention; empty and loading are rendered as content states instead. */
export function getDashboardWidgetIssue(
  status: DashboardWidgetStatus,
  details: DashboardWidgetIssueDetails = {}
): DashboardWidgetIssue | undefined {
  switch (status) {
    case "ready":
    case "loading":
    case "empty":
      return undefined;
    case "not_permitted":
      return {
        severity: "error",
        title: "Access unavailable",
        description: "Your current role is not permitted to access this data source.",
      };
    case "stale":
      return {
        severity: "warning",
        title: "Data may be delayed",
        description: "The displayed values are the most recent readings received.",
      };
    case "unavailable":
      return {
        severity: "error",
        title: "Data source unavailable",
        description:
          details.unavailableAreaNames?.length === 1
            ? `No recent data is available for ${formatAreaNames(details.unavailableAreaNames)}.`
            : "This source did not provide data in the latest dashboard snapshot.",
      };
    case "partial":
      return {
        severity: "warning",
        title: "Some data unavailable",
        description:
          details.notConfiguredAreaNames?.length && details.unavailableAreaNames?.length
            ? `Total Population is not configured for ${formatAreaNames(details.notConfiguredAreaNames)} and has no recent data for ${formatAreaNames(details.unavailableAreaNames)}.`
            : details.notConfiguredAreaNames?.length
              ? `Total Population is not configured for ${formatAreaNames(details.notConfiguredAreaNames)}.`
              : details.unavailableAreaNames?.length
                ? `No recent data is available for ${formatAreaNames(details.unavailableAreaNames)}.`
                : "At least one value is unavailable; other displayed values remain available.",
      };
    case "error":
      return {
        severity: "error",
        title: "Unable to load data",
        description: "The dashboard could not retrieve this source. Try refreshing the page.",
      };
  }
}

export function DashboardWidgetIssueIndicator({ issue }: { issue: DashboardWidgetIssue }) {
  const Icon = issue.severity === "warning" ? WarningAmberOutlinedIcon : InfoOutlinedIcon;

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
            {issue.title}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
            {issue.description}
          </Typography>
        </Box>
      }
    >
      <Box
        component="span"
        aria-label={issue.title}
        sx={(theme) => {
          const palette =
            issue.severity === "error"
              ? theme.palette.error
              : issue.severity === "warning"
                ? theme.palette.warning
                : theme.palette.info;

          return {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            color: palette.light,
            backgroundColor: alpha(palette.main, 0.12),
            border: `1px solid ${alpha(palette.main, 0.35)}`,
            cursor: "help",
          };
        }}
      >
        <Icon sx={{ fontSize: "0.9rem" }} />
      </Box>
    </Tooltip>
  );
}
